import { z } from 'zod';

import { BOOKING_LIMITS } from '../constants/booking';
import {
  AttendanceKindSchema,
  GatheringStatusSchema,
  OrderStatusSchema,
  RsvpSchema,
  TournamentFormatSchema,
  WeekdaySchema,
} from '../enums';
import { IdSchema, KopecksSchema } from './common';
import { ActivityBriefSchema } from './pool';
import { BookingCloseKindSchema, OrderRefSchema } from './order';
import { SeatBlockReasonSchema } from './seat';

/**
 * «Мои сборы» и заявка из нескольких разовых мест с одной оплатой (BLUEPRINT Stage 17, ADR-35).
 * Контракт `bookings.preview/create/status/cancelItem` (задача 2) и `my.gatherings` (задача 4).
 * Заявка — родительский `Order BUNDLE`, позиции — `SINGLE`, `SUBSTITUTE`, `TRIAL`, каждая со своим местом.
 */

/** Что можно положить в заявку. Место в отряде на сезон (`SEASON`, `RESERVE`) — нет: у него гарантия набора. */
export const BookingItemKindSchema = z.enum(['SINGLE', 'SUBSTITUTE', 'TRIAL']);
export type BookingItemKind = z.infer<typeof BookingItemKindSchema>;

/**
 * Позиция выбора: сбор и вид места. У замены есть конкретное открытое место (`seatId`);
 * место `SINGLE` и пробный заводятся сервером в момент брони, поэтому у них `seatId` нет.
 */
export const BookingItemInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('SINGLE'), gatheringId: IdSchema }),
  z.object({ kind: z.literal('TRIAL'), gatheringId: IdSchema }),
  z.object({ kind: z.literal('SUBSTITUTE'), gatheringId: IdSchema, seatId: IdSchema }),
]);
export type BookingItemInput = z.infer<typeof BookingItemInputSchema>;

/**
 * Вход `bookings.preview` и `bookings.create`. Один сбор — одна позиция. Сколько позиций можно
 * на самом деле, решает `Setting booking.maxItems`; здесь только жёсткая граница контракта.
 */
export const BookingInputSchema = z
  .object({
    items: z.array(BookingItemInputSchema).min(1).max(BOOKING_LIMITS.itemsHardMax),
  })
  .refine((v) => new Set(v.items.map((i) => i.gatheringId)).size === v.items.length, {
    path: ['items'],
    message: 'duplicate_gathering',
  });
export type BookingInput = z.infer<typeof BookingInputSchema>;

/** Вход `bookings.status`: id заявки (родительского заказа `BUNDLE`). */
export const BookingIdInputSchema = z.object({ id: IdSchema });
export type BookingIdInput = z.infer<typeof BookingIdInputSchema>;

/**
 * Причины отказа по заявке и позиции (тексты — `texts.errors` Mini App и админки).
 * `single_limit` — лимит разовых без сезона у человека, `single_limit_gathering` — доля разовых
 * на сборе исчерпана (`booking.singleShareMax`) или капитан закрыл сбор.
 */
export const BookingBlockReasonSchema = z.enum([
  'time_conflict',
  'single_limit',
  'single_limit_gathering',
  'hold_expired',
  'feature_disabled',
  // Задача 2 (`bookings.*`): причины позиции и заявки, которых нет среди причин места витрины
  /** Капитан закрыл сбор для разовых (`Gathering.singlesClosed`) */
  'singles_closed',
  /** Отряд или сезон ещё не `ACTIVE` (набор под гарантию): разовых мест нет, ADR-35 */
  'squad_not_launched',
  /** В заявке больше `booking.maxItems` позиций */
  'booking_items_limit',
  /** Отмена позиции позже `booking.cancelFullRefundHours` до начала */
  'cancel_too_late',
  /** Человек уже идёт на этот сбор или держит его в другой заявке */
  'already_going',
  'gathering_closed',
  'gathering_too_soon',
  /** Место замены держит другой человек («слот забрали») */
  'seat_taken',
  'seat_closed',
  'trial_disabled',
  /**
   * Задача 4 (вопрос владельцу 8): позиция уже зачтена в заказ сезона, который не отменён
   * (`creditedToOrderId`) — выписка с возвратом отдала бы те же деньги второй раз
   */
  'credited_to_season',
  /**
   * Раунд персон 1: на этот сбор в живой брони уже лежит другая позиция — новая заявка
   * дописывается в бронь и позицию на тот же сбор не меняет
   */
  'in_booking',
]);
export type BookingBlockReason = z.infer<typeof BookingBlockReasonSchema>;

/** Отказы `admin.tournaments.setFormat` / `assign` (задача 5), только в админке. */
export const TournamentFormatReasonSchema = z.enum([
  'format_locked',
  'format_mismatch',
  'not_tournament',
]);
export type TournamentFormatReason = z.infer<typeof TournamentFormatReasonSchema>;

/** Почему позицию нельзя оплатить: причина заявки или причина места витрины (`squad_full` — «слот забрали»). */
export const BookingItemReasonSchema = z.union([BookingBlockReasonSchema, SeatBlockReasonSchema]);
export type BookingItemReason = z.infer<typeof BookingItemReasonSchema>;

/**
 * Отряд сбора без названия — активностью и вечером (раунд персон 1, M5/N8): «отряд без названия»
 * ни о чём не говорил. Вечер — отряда, а не сбора: доп. сбор бывает в субботу днём.
 */
export const SquadBriefSchema = z.object({
  weekday: WeekdaySchema,
  startTime: z.string(),
  activityName: z.string(),
});
export type SquadBrief = z.infer<typeof SquadBriefSchema>;

/** Позиция заявки для экрана `/booking/:id` и ответа `bookings.preview`. */
export const BookingItemPublicSchema = z.object({
  /** id позиции (`Order`); в `bookings.preview` заказа ещё нет — null */
  orderId: IdSchema.nullable(),
  kind: BookingItemKindSchema,
  gatheringId: IdSchema,
  squadId: IdSchema,
  squadName: z.string().nullable(),
  squadBrief: SquadBriefSchema,
  activity: ActivityBriefSchema,
  startsAt: z.string().datetime(),
  durationMin: z.number().int().positive(),
  /** Таймзона города сбора: время окончания турнира показывается в ней */
  timezone: z.string(),
  venueName: z.string().nullable(),
  /** Цена позиции до бонуса */
  priceKopecks: KopecksSchema,
  /** К оплате по позиции после доли реферального бонуса; сумма позиций = сумма заявки */
  amountKopecks: KopecksSchema,
  status: OrderStatusSchema.nullable(),
  refundedKopecks: KopecksSchema,
  /** Формат турнира позиции (`tournamentFormatOf`): null — не турнир */
  tournamentFormat: TournamentFormatSchema.nullable(),
  /** Позицию нельзя оплатить: слот забрали, пересечение, лимит; null — доступна */
  unavailableReason: BookingItemReasonSchema.nullable(),
  /** С чем пересекается по времени (`time_conflict`) */
  conflictGatheringId: IdSchema.nullable(),
  /** До какого момента позицию можно отменить с полным возвратом (`booking.cancelFullRefundHours`) */
  cancelUntil: z.string().datetime().nullable(),
  /** Человек выписался с оплаченной позиции (`bookings.cancelItem`): деньги ждут возврата ops */
  cancelledAt: z.string().datetime().nullable(),
  /**
   * Задача 7 (стык задачи 6): сбор позиции перенесли на другую дату — запись не переезжает,
   * позиция «к возврату» (`unavailableReason = gathering_closed`); экран пишет «сбор перенесён»
   */
  gatheringRescheduled: z.boolean().default(false),
  /** Раунд персон 1: `bookings.preview` — позиция уже лежит в живой брони (дописывание) */
  fromBooking: z.boolean().default(false),
});
export type BookingItemPublic = z.infer<typeof BookingItemPublicSchema>;

/** Заявка целиком: одна ссылка, одна сумма, бронь `holdUntil`. */
export const BookingPublicSchema = z.object({
  /** id родительского заказа `BUNDLE`; в `bookings.preview` — null */
  id: IdSchema.nullable(),
  status: OrderStatusSchema.nullable(),
  /** Сумма доступных позиций до бонуса */
  listKopecks: KopecksSchema,
  referralBonusKopecks: KopecksSchema,
  /** К оплате одной ссылкой = сумма `amountKopecks` доступных позиций */
  amountKopecks: KopecksSchema,
  holdUntil: z.string().datetime().nullable(),
  paidAt: z.string().datetime().nullable(),
  refundedKopecks: KopecksSchema,
  /** Ссылка Робокассы; при повторе с живой бронью — та же. В ручном режиме оплаты — null */
  invoiceUrl: z.string().nullable(),
  items: z.array(BookingItemPublicSchema),
  createdAt: z.string().datetime().nullable(),
  /**
   * Задача 7: почему заявка закрыта без оплаты (`CANCELLED`) — бронь истекла, человек собрал
   * другую заявку или её закрыл ops в «Заказах» (стык задачи 6); null — заявка не закрыта
   */
  closeReason: BookingCloseKindSchema.nullable().default(null),
  /** Ручной режим оплаты (`invoiceUrl = null`): реквизиты перевода из конфига, null — через поддержку */
  manualDetails: z.string().nullable().default(null),
  /**
   * Раунд персон 1: `bookings.preview` при живой брони — её id; «Оплатить» дописывает выбор в
   * неё: новая заявка из прежних и новых позиций, новая ссылка, прежняя заявка снята
   */
  appendTo: IdSchema.nullable().default(null),
  /** Раунд персон 2 (M8): бронь дописана в новую заявку (`closeReason = replaced`) — ссылка на неё */
  replacedBy: OrderRefSchema.nullable().default(null),
});
export type BookingPublic = z.infer<typeof BookingPublicSchema>;

/**
 * По какому праву человек на сборе в «Моих сборах»: `SQUAD` — сбор своего отряда, остальные —
 * позиции в чужих отрядах. Совпадает с `Attendance.kind`.
 */
export const MyGatheringRelationSchema = AttendanceKindSchema;
export type MyGatheringRelation = z.infer<typeof MyGatheringRelationSchema>;

/**
 * Строка «Моих сборов». Признаки вкладок-фильтров (`relation`, `isOpsExtra`, `isTournament`,
 * `tournamentFormat`) считает сервер функцией `myGatheringTags`; клиент по ним только фильтрует —
 * так подходит любой состав вкладок (решение владельца о вкладках не зашито в контракт).
 */
export const MyGatheringItemSchema = z.object({
  gatheringId: IdSchema,
  squadId: IdSchema,
  squadName: z.string().nullable(),
  squadBrief: SquadBriefSchema,
  activity: ActivityBriefSchema,
  startsAt: z.string().datetime(),
  durationMin: z.number().int().positive(),
  timezone: z.string(),
  venueName: z.string().nullable(),
  status: GatheringStatusSchema,
  relation: MyGatheringRelationSchema,
  /** Сбор создал ops (`Gathering.opsCreated`) — и для участника отряда, и для разового */
  isOpsExtra: z.boolean(),
  isTournament: z.boolean(),
  tournamentFormat: TournamentFormatSchema.nullable(),
  /** Ответ на сбор своего отряда; у разовых — null */
  rsvp: RsvpSchema.nullable(),
  /** Позиция, по которой человек на сборе (разовые); у сбора своего отряда — null */
  orderId: IdSchema.nullable(),
  /** Заявка позиции (`/booking/:id`); у одиночного заказа Stage 11 и сбора своего отряда — null */
  bookingId: IdSchema.nullable(),
  amountKopecks: KopecksSchema.nullable(),
  /**
   * До какого момента можно выписаться с полным возвратом (`bookings.cancelItem`): только у
   * оплаченной позиции заявки, не зачтённой в сезон; иначе null — кнопки нет
   */
  cancelUntil: z.string().datetime().nullable(),
});
export type MyGatheringItem = z.infer<typeof MyGatheringItemSchema>;

/**
 * Ответ `my.gatherings()`: все будущие записи одним списком по времени и живые брони над
 * вкладками. С выключенным `feature.myGatherings` процедура отвечает `feature_disabled`.
 */
export const MyGatheringsResultSchema = z.object({
  items: z.array(MyGatheringItemSchema),
  bookings: z.array(BookingPublicSchema),
});
export type MyGatheringsResult = z.infer<typeof MyGatheringsResultSchema>;
